import type { ApiCallResponse } from 'api/types';
import { getCookie } from 'utils/cookie';
import { WtrRootState } from 'redux/create-store';
import { COOKIE_MONETATE_ID } from 'constants/cookies';
import banner, { v3favourites, v3search, offers } from 'api/definitions/adtech';
import { getCustomerId, getCustomerOrderId } from 'redux/modules/sessions/selectors';
import { apiPromiseTypes } from 'redux/middleware/api-promise/action-types';
import type {
  BannerResponse,
  BannerV3FavouritesRequest,
  BannerV3Request,
} from 'api/definitions/adtech/types';
import {
  ADTECH_BANNERS,
  ADTECH_LEADERBOARD,
  ADTECH_OFFERS,
  ADTECH_OFFERS_LEADERBOARDS,
} from './types';
import { selectLeaderboard } from '../selectors';

const getHeaders = (state: WtrState) => ({
  'monetate-id': getCookie(COOKIE_MONETATE_ID) || '',
  'customer-id': getCustomerId(state),
});

export const getBanner =
  (categories: string[]): WtrThunkAction<Promise<void | ApiCallResponse<BannerResponse>>> =>
  (dispatch, getState) =>
    dispatch({
      types: ADTECH_BANNERS.triplet,
      apiCall: banner.post({
        body: {
          type: 'MEGA_MENU',
          categories,
          style: 'traffic_driver',
          trolleyId: getCustomerOrderId(getState()),
        },
        iHandleStatusCodes: true,
      }),
    }).catch(() => undefined) as Promise<void | ApiCallResponse<BannerResponse>>;

export const getLeaderboardBanner =
  (search: string): WtrThunkAction<Promise<void | ApiCallResponse<BannerResponse>>> =>
  (dispatch, getState) => {
    const searchTerm = search.toLowerCase();

    return dispatch({
      types: ADTECH_LEADERBOARD.triplet,
      apiCall: v3search.post({
        body: {
          searchTerm,
          style: 'leaderboard',
          trolleyId: getCustomerOrderId(getState()),
          maxNumberOfAds: 1,
        } as BannerV3Request,
        headers: getHeaders(getState()),
        iHandleStatusCodes: true,
      }),
      searchTerm,
    }).catch(() => undefined) as Promise<void | ApiCallResponse<BannerResponse>>;
  };

export const getFavouritesLeaderboardBanner =
  (keyword: string): WtrThunkAction<Promise<void | ApiCallResponse<BannerResponse>>> =>
  (dispatch, getState) => {
    const searchTerm = keyword.toLowerCase();
    const state = getState();

    if (selectLeaderboard(state as WtrRootState, searchTerm)) {
      return Promise.resolve();
    }

    return dispatch({
      types: ADTECH_LEADERBOARD.triplet,
      apiCall: v3favourites.post({
        body: {
          keyword,
          style: 'LEADERBOARD',
          trolleyId: getCustomerOrderId(state),
        } as BannerV3FavouritesRequest,
        headers: getHeaders(state),
        iHandleStatusCodes: true,
      }),
      searchTerm,
    }).catch(() => undefined) as Promise<void | ApiCallResponse<BannerResponse>>;
  };

const createCall =
  (
    apiDefn: {
      post: (config: {
        body: Record<string, string | number | undefined>;
        headers?: Record<string, string>;
        iHandleStatusCodes?: boolean;
      }) => WtrApiCall<void | ApiCallResponse<BannerResponse>>;
    },
    types: ReturnType<typeof apiPromiseTypes>,
    body: Record<string, string | number | undefined>,
  ): WtrThunkAction<Promise<void | ApiCallResponse<BannerResponse>>> =>
  (dispatch, getState) =>
    dispatch({
      types: types.triplet,
      apiCall: apiDefn.post({
        body: {
          ...body,
          trolleyId: getCustomerOrderId(getState()),
        },
        headers: getHeaders(getState()),
        iHandleStatusCodes: true,
      }),
    });

export const getHotspots: WtrThunkAction<Promise<void | ApiCallResponse<BannerResponse>>> = (
  dispatch,
  getState,
) =>
  dispatch({
    types: ADTECH_OFFERS.triplet,
    apiCall: offers.post({
      body: {
        style: 'HOTSPOT',
        trolleyId: getCustomerOrderId(getState()),
        maxNumberOfAds: 8,
      },
      headers: getHeaders(getState()),
      iHandleStatusCodes: true,
    }),
  });

export const getOffersLeaderboards = createCall(offers, ADTECH_OFFERS_LEADERBOARDS, {
  style: 'LEADERBOARD',
  maxNumberOfAds: 2,
});
